import React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import s from './styles.module.scss'

import DepositSection from '../../upload-quotation-modal/deposit-section/DepositSection'
import { getMinDepositRate } from '../../upload-quotation-modal/deposit-section/utils/get-min-deposit-rate'
import { CompanyInformationClientProposalFragment } from '../../../../../../../../lib/graphql'
import { multiplyByFactor } from '../../../../../../../../utils/prices'
import { CurrencyAreaContextProvider } from '../../../../../../../contexts'
import { Modal } from '../../../../../../../molecules'
import { BillingColumns } from '../../../../../../../organisms/proposal/proposal-details/details-container/BillingColumns'
import { DescriptionMode } from '../../../../../../../organisms/proposal/proposal-details/details-container/DescriptionMode'
import DetailsTotalOwner from '../../../../../../../organisms/proposal/proposal-details/details-container/details-total-owner/DetailsTotalOwner'
import DetailsContainer from '../../../../../../../organisms/proposal/proposal-details/details-container/DetailsContainer'

export interface IDepositFormValues {
  isConfirmed: boolean
  depositRate: string
}

const OwnerCanConfirmBookingModal = ({
  clientProposal,
  isOpen,
  setIsOpen,
  ownerConfirmedBooking,
}: {
  clientProposal: CompanyInformationClientProposalFragment
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  ownerConfirmedBooking: {
    callback: (args: { depositRate: number }) => void
    isLoading: boolean
  }
}) => {
  const intl = useIntl()
  const totalUser = clientProposal.price?.totalUser
  const ownerFeesPrice = totalUser?.feesWithDiscount?.ownerFeesPrice
  const priceWithDiscount = totalUser?.priceWithDiscount
  const priceWithDiscountAndOwnerFees = totalUser?.priceWithDiscountAndOwnerFees

  if (!ownerFeesPrice || !priceWithDiscount || !priceWithDiscountAndOwnerFees) {
    return null
  }

  const {
    control,
    handleSubmit,
    formState: { isValid },
    getValues,
  } = useForm<IDepositFormValues>({
    mode: 'onChange',
    defaultValues: {
      isConfirmed: false,
    },
  })

  const ProposalDetailsTotalColumns = [
    BillingColumns.description,
    BillingColumns.totalHT,
    BillingColumns.totalTTC,
  ]

  return (
    <Modal
      isOpen={isOpen}
      title={intl.formatMessage({
        id: 'ui.OwnerCanConfirmBookingModal.acceptAnd defineDeposit',
        defaultMessage: "Accepter la réservation et définir l'acompte",
      })}
      styleHeader={{ marginBottom: '20px' }}
      footer={{
        button: {
          label: intl.formatMessage({
            id: 'ui.OwnerCanConfirmBookingModal.confirmBooking',
            defaultMessage: 'Confirmer la réservation',
          }),
          fullWidth: true,
          onClick: handleSubmit(async (formValues) => {
            await ownerConfirmedBooking.callback({
              depositRate: multiplyByFactor(
                Math.max(
                  parseInt(formValues.depositRate || '0'),
                  getMinDepositRate(clientProposal.startDate),
                ),
              ),
            })
            setIsOpen(false)
          }),
          disabled: !isValid,
          isLoading: ownerConfirmedBooking.isLoading,
        },
      }}
      scrollbarStyle={{
        display: 'flex',
        flexDirection: 'column',
        paddingInline: '30px',
        paddingTop: 0,
      }}
      closeModal={() => setIsOpen(false)}
      footerStyle={{ boxSizing: 'border-box' }}
    >
      <CurrencyAreaContextProvider
        context={{ currencyArea: clientProposal.house?.currencyArea }}
      >
        <div className={s.content}>
          <DepositSection
            control={control}
            startDate={clientProposal.startDate}
            depositRate={getValues('depositRate')}
          />
          <DetailsContainer
            title={intl.formatMessage({
              id: 'ui.OwnerCanConfirmBookingModal.total',
              defaultMessage: 'Total',
            })}
            columns={ProposalDetailsTotalColumns}
            descriptionMode={DescriptionMode.hidden}
          >
            <DetailsTotalOwner
              total={{
                price: priceWithDiscount,
                ownerFees: ownerFeesPrice,
                priceOwner: priceWithDiscountAndOwnerFees,
              }}
              className={s.detailsTotal}
              columns={ProposalDetailsTotalColumns}
              descriptionMode={DescriptionMode.toggable}
              showAdjustments={false}
            />
          </DetailsContainer>
        </div>
      </CurrencyAreaContextProvider>
    </Modal>
  )
}

export default OwnerCanConfirmBookingModal
